/**
 * Auth Service for prosci_portal
 *
 * This service handles backend session management.
 * Auth0 authentication is handled by @auth0/auth0-react SDK.
 *
 * Flow:
 * 1. User authenticates via Auth0 (@auth0/auth0-react handles this)
 * 2. Frontend gets user.sub from Auth0
 * 3. createSecureSession() sends auth0_sub to backend
 * 4. Backend creates JWT session cookie
 * 5. All subsequent API calls use the cookie automatically
 */

import apiClient, { clearCsrfToken, setCsrfToken } from '../utils/apiClient';

class AuthService {
  /**
   * Create a secure backend session after Auth0 authentication
   * The backend verifies the Auth0 ID token and takes the user's identity from
   * it; the other fields are only a fallback for the rollout.
   */
  async createSecureSession(auth0Sub: string, email?: string, givenName?: string, familyName?: string, auth0Sid?: string, idToken?: string) {
    try {
      const response = await apiClient.post('/api/auth/token', {
        auth_type: 'auth0_portal',
        id_token: idToken,
        auth0_sub: auth0Sub,
        email: email,
        given_name: givenName,
        family_name: familyName,
        auth0_sid: auth0Sid
      });

      // Store CSRF token in memory (not localStorage!)
      const csrfToken = response.data.data.csrf_token;
      if (csrfToken) {
        setCsrfToken(csrfToken);
      }

      return response.data.data.user;
    } catch (error: any) {
      // Preserve specific error information for AuthContext
      if (error.response) {
        const enhancedError = new Error(error.response.data?.message || 'Authentication failed');
        (enhancedError as any).response = error.response;
        throw enhancedError;
      }

      console.error('Failed to create secure session:', error);
      throw error;
    }
  }

  /**
   * Validate the current session with the backend
   * The backend validates the JWT in the httpOnly cookie
   */
  async validateSession(): Promise<boolean> {
    try {
      // If we can make an authenticated request successfully, session is valid
      // Skip 401 handling to prevent redirect loops
      // Session-validation only needs to confirm a user is returned, so request
      // the smallest possible serializer profile.
      const response = await apiClient.get('/api/users/current', { params: { profile: 'minimal' }, skip401Handling: true });
      // Check for actual user data in JSON:API format (response.data.data)
      // An empty response {} would have response.data truthy but response.data.data undefined
      return !!response.data?.data;
    } catch {
      return false;
    }
  }

  /**
   * Destroy the backend session
   * This clears the JWT cookie on the server
   */
  async destroySession(): Promise<void> {
    try {
      await apiClient.delete('/api/auth/token');
      clearCsrfToken();
    } catch (error) {
      console.error('Failed to destroy session:', error);
      // Still clear CSRF token even if API call fails
      clearCsrfToken();
    }
  }

  /**
   * Clear local session data (CSRF token)
   */
  clearSession(): void {
    clearCsrfToken();
  }

  /**
   * Check if we have an active session
   * Note: This is a best-effort check - actual validation requires API call
   */
  hasActiveSession(): boolean {
    // Session is in httpOnly cookie, can't check directly
    // Will be validated by validateSession() instead
    return false;
  }
}

export default new AuthService();
